/**
 * Scale/Edit modal – center creative column (senders + texts side by side).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo } from 'react';
import { MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';
import { AnimatePresence } from 'framer-motion';

import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import { AiGenerateSendersDropdown } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { GeneratedSendersPreview } from '@/features/campaign-texts/components/GeneratedSendersPreview';
import { GeneratedSendersPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedSendersPreviewPerDrop';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { GeneratedTextsPreview } from '@/features/campaign-texts/components/GeneratedTextsPreview';
import { GeneratedTextsPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedTextsPreviewPerDrop';
import { useAuth } from '@/providers/AuthProvider';
import {
  SendersPickerSection,
  TextsPickerSection,
} from '@/features/smswiser-campaigns/components/drop-modals';
import type { PerformanceTag } from '@/features/smswiser-campaigns/components/drop-modals/drop-modals.types';
import { ColumnHeader } from '../../../shared';
import type { useScaleDropForm } from '../../../useScaleDropForm';
import type { ScaleDropModalProps } from '../../../ScaleDropModal.types';

import { useAiTextGeneration } from './useAiTextGeneration';
import { useAiSenderGeneration } from './useAiSenderGeneration';

interface Props {
  isMulti: boolean;
  dropCount: number;
  rows: DropsWithStatsRow[];
  form: ReturnType<typeof useScaleDropForm>;
  isBusy: boolean;
  countryKey: string;
  onAddSender?: ScaleDropModalProps['onAddSender'];
  onAddText?: ScaleDropModalProps['onAddText'];
  onDeleteTextsBulk?: ScaleDropModalProps['onDeleteTextsBulk'];
  tagsByTextId?: Record<string, PerformanceTag[]>;
  tagColors?: Record<string, string>;
  smsTextsWithIds?: ScaleDropModalProps['smsTextsWithIds'];
}

const NOOP_ADD = async (_t: string[]) => {};

function buildDropLabelsFromRows(rows: DropsWithStatsRow[]): DropLabel[] {
  return rows.map((row, i) => {
    const d = row.drop;
    const id = String(d?.uniqueId ?? d?.id ?? i);
    const label = d?.leadsList ?? d?.title ?? `Drop ${i + 1}`;
    return { id, label };
  });
}

export function ScaleOrEditCreativeColumn(props: Props) {
  const { isMulti, dropCount, rows, form, isBusy, countryKey } = props;
  const dropLabels = useMemo(() => (isMulti && rows.length > 0 ? buildDropLabelsFromRows(rows) : undefined), [isMulti, rows]);
  const { hasPermission } = useAuth();
  const showAi = Boolean(props.onAddText) && hasPermission('campaign_texts.generate');
  const showAiSenders = Boolean(props.onAddSender) && hasPermission('campaign_senders.generate');

  const onSaveGeneratedPerDropScale = useCallback(
    async (batches: string[][]) => {
      form.setPerDropSmsTexts(batches);
    },
    [form],
  );

  const ai = useAiTextGeneration({
    countryKey,
    onAddTexts: props.onAddText ?? NOOP_ADD,
    onSaveGeneratedAndSelectOnly: form.saveGeneratedAndSelectOnly,
    onMarkTextsForHide: form.markTextsForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSaveGeneratedPerDropScale : undefined,
    onAiGeneratedDeselectOld: form.clearTextsSelection,
    onAfterSave: () => {
      form.setShowTexts(false);
      form.setShowSenders(true);
    },
  });

  const onSaveGeneratedSendersPerDropScale = useCallback(
    async (batches: string[][]) => {
      form.setPerDropSenderNames(batches);
    },
    [form],
  );

  const aiSenders = useAiSenderGeneration({
    countryKey,
    onAddSenders: props.onAddSender ? async (s) => { for (const n of s) await props.onAddSender!(n); } : async () => {},
    onSaveGeneratedAndSelectOnly: form.saveGeneratedSendersAndSelectOnly,
    onMarkSendersForHide: form.markSendersForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSaveGeneratedSendersPerDropScale : undefined,
    onPerDropSendersApplied: (batches) => form.setPerDropSenderNames(batches),
    onAiGeneratedDeselectOld: form.clearSendersSelection,
    onAfterSave: () => {
      form.setShowSenders(false);
      form.setShowTexts(true);
    },
  });

  const hasAiSenders = Boolean(
    (aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) || aiSenders.generatedSenders.length > 0,
  );
  const hasAiTexts = Boolean(
    (ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0,
  );
  const sendersTakeFullSpace = hasAiSenders || !form.showTexts;
  const textsTakeFullSpace = hasAiTexts || !form.showSenders;

  return (
    <>
      <ColumnHeader icon={MessageSquare} title="Creative" subtitle="Senders & Texts" />
      <div
        className={cn(
          'scale-creative-grid min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3',
          sendersTakeFullSpace && 'scale-creative-grid-ai-senders',
          textsTakeFullSpace && !sendersTakeFullSpace && 'scale-creative-grid-ai-texts',
          !form.showSenders && form.showTexts && 'scale-creative-grid-senders-collapsed',
          form.showSenders && !form.showTexts && 'scale-creative-grid-texts-collapsed',
        )}
      >
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <SendersPickerSection
            sendersList={form.sendersList}
            selectedSenders={form.selectedSenderNames}
            onToggleSender={form.toggleSender}
            onSelectAll={form.selectAllSenders}
            onClearSelection={form.clearSendersSelection}
            onDeleteSender={form.handleDeleteSender}
            onDeleteSenders={form.handleDeleteSenders}
            search={form.senderSearch}
            onSearch={form.setSenderSearch}
            newValue={form.newSenderValue}
            setNewValue={form.setNewSenderValue}
            onAdd={props.onAddSender ? form.handleAddSender : undefined}
            isBusy={isBusy}
            expanded={form.showSenders}
            onToggleExpand={() => form.setShowSenders((v) => !v)}
            templates={form.senderTemplates}
            activeTemplateId={form.activeTemplateId}
            onSelectTemplate={form.handleSelectTemplate}
            onCreateTemplate={form.handleSaveAsTemplate}
            canSaveTemplate={form.canSaveTemplate}
            pendingHideSenders={form.pendingHideSenders}
            replacedByAiSenders={form.replacedByAiSenders}
            actionSlot={
              showAiSenders ? (
                <AiGenerateSendersDropdown
                  countryKey={countryKey}
                  dropCount={dropCount}
                  dropLabels={dropLabels}
                  onGenerated={aiSenders.handleAiGenerated}
                />
              ) : undefined
            }
            toolbarSlot={
              showAiSenders ? (
                <AnimatePresence>
                  {aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0 ? (
                    <GeneratedSendersPreviewPerDrop
                      batches={aiSenders.perDropBatches}
                      groupIdsPerDrop={aiSenders.groupIdsPerDrop}
                      dropLabels={dropLabels}
                      onSave={aiSenders.handleSavePerDrop}
                      onDismiss={aiSenders.handleDismissPerDrop}
                      onRegenerate={aiSenders.handleRegeneratePerDrop}
                      onRegenerateAll={aiSenders.handleRegenerateAllPerDrop}
                      onSaveAndHide={aiSenders.handleSaveAndHidePerDrop}
                      isRegenerating={aiSenders.isRegenerating}
                      useAndHideOnly={dropCount > 1}
                      applied={aiSenders.perDropApplied}
                      generationTimeMs={aiSenders.perDropGenerationTimeMs}
                      fillHeight
                    />
                  ) : aiSenders.generatedSenders.length > 0 ? (
                    <GeneratedSendersPreview
                      senders={aiSenders.generatedSenders}
                      onSave={aiSenders.handleSaveGenerated}
                      onSaveAndHide={aiSenders.handleSaveAndHide}
                      onDismiss={aiSenders.handleDismiss}
                      onRegenerate={aiSenders.handleRegenerate}
                      isRegenerating={aiSenders.isRegenerating}
                      fillHeight
                    />
                  ) : null}
                </AnimatePresence>
              ) : undefined
            }
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <TextsPickerSection
            textsList={form.textsList}
            selectedTexts={form.selectedSmsTexts}
            onToggleText={form.toggleText}
            onSelectAll={form.selectAllTexts}
            onClearSelection={form.clearTextsSelection}
            onDeleteText={form.handleDeleteText}
            onDeleteTextsBulk={props.onDeleteTextsBulk ? form.handleDeleteTextsBulk : undefined}
            search={form.textSearch}
            onSearch={form.setTextSearch}
            newValue={form.newTextValue}
            setNewValue={form.setNewTextValue}
            onAdd={props.onAddText ? form.handleAddText : undefined}
            isBusy={isBusy}
            expanded={form.showTexts}
            onToggleExpand={() => form.setShowTexts((v) => !v)}
            tagsByTextId={props.tagsByTextId}
            tagColors={props.tagColors}
            smsTextsWithIds={props.smsTextsWithIds}
            pendingHideTexts={form.pendingHideTexts}
            replacedByAiTexts={form.replacedByAiTexts}
            actionSlot={
              showAi ? (
                <AiGenerateDropdown
                  countryKey={countryKey}
                  dropCount={dropCount}
                  dropLabels={dropLabels}
                  existingBatches={
                    ai.perDropBatches?.length === dropCount ? ai.perDropBatches : undefined
                  }
                  onOpen={() => form.setShowSenders(false)}
                  onGenerated={ai.handleAiGenerated}
                />
              ) : undefined
            }
            toolbarSlot={
              showAi ? (
                <AnimatePresence>
                  {ai.perDropBatches && ai.perDropBatches.length > 0 ? (
                    <GeneratedTextsPreviewPerDrop
                      batches={ai.perDropBatches}
                      translationsPerDrop={ai.perDropTranslations}
                      groupIdsPerDrop={ai.groupIdsPerDrop}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      dropLabels={dropLabels}
                      onSave={ai.handleSavePerDrop}
                      onDismiss={ai.handleDismissPerDrop}
                      onRegenerate={ai.handleRegeneratePerDrop}
                      onRegenerateAll={ai.handleRegenerateAllPerDrop}
                      onRegenerateOverLength={ai.handleRegenerateOverLengthPerDrop}
                      onRegenerateFailed={ai.handleRegenerateFailedPerDrop}
                      onSaveAndHide={ai.handleSaveAndHidePerDrop}
                      onTranslatePerDropAll={ai.handleTranslatePerDropAll}
                      onTranslatePerDropMissing={ai.handleTranslatePerDropMissing}
                      onTranslateAllPerDrop={ai.handleTranslateAllPerDrop}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                      useAndHideOnly={dropCount > 1}
                      applied={ai.perDropApplied}
                      generationTimeMs={ai.perDropGenerationTimeMs}
                    />
                  ) : ai.generatedTexts.length > 0 ? (
                    <GeneratedTextsPreview
                      texts={ai.generatedTexts}
                      translations={ai.generatedTranslations}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      onSave={ai.handleSaveGenerated}
                      onSaveAndHide={ai.handleSaveAndHide}
                      onDismiss={ai.handleDismiss}
                      onRegenerate={ai.handleRegenerate}
                      onRegenerateOverLength={ai.handleRegenerateOverLength}
                      onTranslateAll={ai.handleTranslateAll}
                      onTranslateMissing={ai.handleTranslateMissing}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                    />
                  ) : null}
                </AnimatePresence>
              ) : undefined
            }
          />
        </div>
      </div>
    </>
  );
}
